import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnDestroy, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ApplicationComponent } from '../../shared/base';
import { Endpoints } from '@core/api';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { ResponsiveService, SelectionService } from '../../shared/services';
import { UserRole } from '../../auth/enums';
import { TokenService } from '../../auth/services/token.service';
import { AuthUser } from '../../auth/models';
import { Brand, Region } from '../../core/organizations/page/models/organization.model';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-nav',
    standalone: true,
    imports: [
        RouterModule,
        CommonModule,
        MatDialogModule,
        MatMenuModule,
        MatButtonModule,
        MatIconModule,
        TranslateModule,
        MatSelectModule,
        FormsModule,
        MatDividerModule,
    ],
    templateUrl: './nav.component.html',
    styleUrl: './nav.component.sass',
})
export class NavComponent extends ApplicationComponent implements OnDestroy {
    protected responsive = inject(ResponsiveService);
    private tokenService = inject(TokenService);
    private selectionService = inject(SelectionService);

    @Input() isCollapsed = true;
    @Output() collapseChanged = new EventEmitter<boolean>();

    userRole = UserRole;

    selectedBrandId: number | null = null;
    selectedRegionId: number | null = null;
    selectedBrandName = 'AethyrTech';

    brands: Brand[] = [];
    regions: Region[] = [];
    user: AuthUser | null = null;

    private authSub: Subscription;
    private stateSub: Subscription;

    constructor() {
        super();
        this.stateSub = this.selectionService.state$.subscribe((state) => {
            this.selectedBrandId = state.brandId;
            this.selectedRegionId = state.regionId;
            this.updateRegionsDropdown();
            this.updateBrandName();
        });

        this.authSub = this.tokenService.getSession().subscribe((auth) => {
            if (auth?.user) {
                this.user = auth.user;
                if (this.user.user_type === 'ORGANIZATION') {
                    this.loadBrands();
                }
            }
        });
    }

    openProfile(): void {
        this.navigateTo('/dashboard/account/profile');
    }

    protected override onViewInit(): void {
        // Initialization handled in subscriptions
    }

    override ngOnDestroy(): void {
        if (this.authSub) this.authSub.unsubscribe();
        if (this.stateSub) this.stateSub.unsubscribe();
    }

    onBrandChange(brandId: number) {
        this.selectedBrandId = Number(brandId);
        this.updateRegionsDropdown();

        // Auto-select first region if available
        this.selectedRegionId = this.regions.length > 0 ? (this.regions[0].id ?? null) : null;
        const brand = this.brands.find((b) => b.id === this.selectedBrandId);
        const region = this.regions.find((r) => r.id === this.selectedRegionId);
        this.selectionService.setSelection(
            this.selectedBrandId,
            this.selectedRegionId,
            brand?.name,
            region?.name,
        );
    }

    onRegionChange(regionId: number) {
        this.selectedRegionId = Number(regionId);
        const brand = this.brands.find((b) => b.id === this.selectedBrandId);
        const region = this.regions.find((r) => r.id === this.selectedRegionId);
        this.selectionService.setSelection(
            this.selectedBrandId,
            this.selectedRegionId,
            brand?.name,
            region?.name,
        );
    }

    private updateRegionsDropdown() {
        if (!this.selectedBrandId || this.brands.length === 0) {
            this.regions = [];
            return;
        }
        const brand = this.brands.find((b) => b.id === this.selectedBrandId);
        this.regions = brand ? brand.regions || [] : [];
    }

    private updateBrandName() {
        if (!this.selectedBrandId || this.brands.length === 0) {
            this.selectedBrandName = 'AethyrTech';
            return;
        }
        const brand = this.brands.find((b) => b.id === this.selectedBrandId);
        this.selectedBrandName = brand ? brand.name : 'AethyrTech';
    }

    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
        this.collapseChanged.emit(this.isCollapsed);
    }

    logout(): void {
        localStorage.clear();
        this.navigateTo('');
    }

    protected loadBrands(): void {
        this.request<Brand[]>(this.http.get<Brand[]>(`${Endpoints.USER_BRANDS}`), {
            showToast: false,
            onSuccess: (data) => {
                if (data && data.length > 0) {
                    this.brands = data;
                    const currentSelection = this.selectionService.getCurrentSelection();

                    let targetBrandId = currentSelection.brandId;
                    let targetRegionId = currentSelection.regionId;

                    // Validate if selected brand still exists in the list
                    const brandExists = this.brands.some((b) => b.id === targetBrandId);
                    if (!brandExists) {
                        targetBrandId = this.brands[0].id ?? null;
                        const defaultRegions = this.brands[0].regions || [];
                        targetRegionId =
                            defaultRegions.length > 0 ? (defaultRegions[0].id ?? null) : null;
                    } else {
                        // Validate if region still exists in that brand
                        const brand = this.brands.find((b) => b.id === targetBrandId);
                        const regionExists = (brand?.regions || []).some(
                            (r: Region) => r.id === targetRegionId,
                        );
                        if (!regionExists) {
                            targetRegionId =
                                brand?.regions && brand.regions.length > 0
                                    ? (brand.regions[0].id ?? null)
                                    : null;
                        }
                    }

                    // Push confirmed state back to service
                    if (
                        targetBrandId !== currentSelection.brandId ||
                        targetRegionId !== currentSelection.regionId ||
                        !currentSelection.brandName ||
                        !currentSelection.regionName
                    ) {
                        const brand = this.brands.find((b) => b.id === targetBrandId);
                        const region = (brand?.regions || []).find(
                            (r: Region) => r.id === targetRegionId,
                        );
                        this.selectionService.setSelection(
                            targetBrandId,
                            targetRegionId,
                            brand?.name,
                            region?.name,
                        );
                    } else {
                        this.updateRegionsDropdown();
                        this.updateBrandName();
                    }
                } else {
                    this.brands = [];
                    this.regions = [];
                }
            },
        });
    }
}
